import React, { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import {
    getLanguageFromPath,
    getRouteIdFromSlug,
    generateAlternateUrls,
    getCanonicalUrl,
    LANGUAGE_PREFIXES
} from '../../config/routes'

/**
 * SEOHead - Dynamic SEO meta tags component
 * Updates canonical, hreflang, and html lang based on current route/language
 */
const SEOHead = ({ title, description }) => {
    const location = useLocation()

    useEffect(() => {
        const language = getLanguageFromPath(location.pathname)

        // Update html lang attribute
        document.documentElement.lang = language === 'pt-BR' ? 'pt-BR' : language.split('-')[0]

        // Get route ID from current path
        let slug = location.pathname
        const prefix = LANGUAGE_PREFIXES[language]
        if (prefix && slug.startsWith(prefix)) {
            slug = slug.substring(prefix.length)
        }
        if (slug.startsWith('/')) {
            slug = slug.substring(1)
        }
        slug = slug.replace(/\/$/, '')

        const routeId = getRouteIdFromSlug(slug, language) || 'home'

        // Update canonical URL
        const canonicalUrl = getCanonicalUrl(routeId, language)
        let canonicalLink = document.querySelector('link[rel="canonical"]')
        if (!canonicalLink) {
            canonicalLink = document.createElement('link')
            canonicalLink.setAttribute('rel', 'canonical')
            document.head.appendChild(canonicalLink)
        }
        canonicalLink.setAttribute('href', canonicalUrl)

        // Update hreflang tags
        // First, remove existing hreflang links
        document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove())

        // Add new hreflang links
        const alternates = generateAlternateUrls(routeId)
        alternates.forEach(({ language: lang, url }) => {
            const link = document.createElement('link')
            link.setAttribute('rel', 'alternate')
            link.setAttribute('hreflang', lang)
            link.setAttribute('href', url)
            document.head.appendChild(link)
        })

        // Add x-default (points to Portuguese as default)
        const xDefaultLink = document.createElement('link')
        xDefaultLink.setAttribute('rel', 'alternate')
        xDefaultLink.setAttribute('hreflang', 'x-default')
        xDefaultLink.setAttribute('href', getCanonicalUrl(routeId, 'pt-BR'))
        document.head.appendChild(xDefaultLink)

        // Update og:url
        const ogUrl = document.querySelector('meta[property="og:url"]')
        if (ogUrl) {
            ogUrl.setAttribute('content', canonicalUrl)
        }

        // Update og:locale
        const ogLocale = document.querySelector('meta[property="og:locale"]')
        if (ogLocale) {
            const localeMap = {
                'pt-BR': 'pt_BR',
                'en-US': 'en_US',
                'es-ES': 'es_ES'
            }
            ogLocale.setAttribute('content', localeMap[language] || 'pt_BR')
        }

    }, [location.pathname])

    // Update title and description if provided
    useEffect(() => {
        if (title) {
            document.title = title

            // Update og:title
            const ogTitle = document.querySelector('meta[property="og:title"]')
            if (ogTitle) {
                ogTitle.setAttribute('content', title)
            }

            // Update twitter:title
            const twitterTitle = document.querySelector('meta[name="twitter:title"]')
            if (twitterTitle) {
                twitterTitle.setAttribute('content', title)
            }
        }

        if (description) {
            // Update meta description
            const metaDesc = document.querySelector('meta[name="description"]')
            if (metaDesc) {
                metaDesc.setAttribute('content', description)
            }

            // Update og:description
            const ogDesc = document.querySelector('meta[property="og:description"]')
            if (ogDesc) {
                ogDesc.setAttribute('content', description)
            }

            // Update twitter:description
            const twitterDesc = document.querySelector('meta[name="twitter:description"]')
            if (twitterDesc) {
                twitterDesc.setAttribute('content', description)
            }
        }
    }, [title, description])

    return null // This component only manages head elements
}

export default SEOHead
